import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import Gala from "../components/gala";

const FACILITY_ID = "SPdqELFz2ovWSn8eFyws";

export default function GalaTours({ isToogle }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);

  // Zakładki: Jastrzębie / Wrocław
  const [activeTab, setActiveTab] = useState("jastrzebie"); // "jastrzebie" | "wroclaw"

  // Modal ustawiania tury
  const [tourGroup, setTourGroup] = useState(null);
  const [tourInput, setTourInput] = useState("");

  // Modal ustawiania czasu występu
  const [timeGroup, setTimeGroup] = useState(null);
  const [timeInput, setTimeInput] = useState("");

  // ---- DATA LOAD ----
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users2026"), (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data()) || [];
      const clients = [];

      for (let i = 0; i < data.length; i++) {
        const subUsers = Object.values(data[i]?.subUsers || {});
        for (let x = 0; x < subUsers.length; x++) {
          clients.push({
            uid: data[i]?.uid,
            acc: data[i],
            user: subUsers[x],
          });
        }
      }

      setUsers(clients);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "groups", FACILITY_ID), (snapshot) => {
      const data = snapshot.data() || {};
      const groupsArr = Object.entries(data).map(([id, value]) => ({
        id,
        ...value,
      }));
      setGroups(groupsArr);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "locations"), (snapshot) => {
      const data = snapshot.docs.map((doc) => doc.data()) || [];
      const flat = [];
      data.forEach((locationDoc) => {
        flat.push(...Object.values(locationDoc || {}));
      });
      setLocations(flat);
    });

    return () => unsub();
  }, []);

  // ---- HELPERS ----
  const numberToPolishWeekday = (num) => {
    const days = {
      1: "Poniedziałek",
      2: "Wtorek",
      3: "Środa",
      4: "Czwartek",
      5: "Piątek",
      6: "Sobota",
      7: "Niedziela",
    };
    return days[num] || "";
  };

  const getSingleGroupGalaStatus = (u, groupId) => {
    const value = u?.user?.gala?.[groupId];
    if (value === true) return "green";
    if (value === false) return "red";
    return "black";
  };

  const isJastrzebieLocation = (locationName = "") => {
    const name = String(locationName).toLowerCase();
    return (
      name.includes("mszana") ||
      name.includes("północna") ||
      name.includes("harcerska")
    );
  };

  const getGroupLabel = (group) => {
    const locationName =
      locations?.find((x) => x?.id === group?.location_id)?.name || "Brak lokalizacji";

    const firstDay = Array.isArray(group?.days) ? group?.days?.[0] : group?.days;

    const dayInfo = firstDay?.custom_name
      ? firstDay.custom_name
      : `${numberToPolishWeekday(firstDay?.weekDay)} ${firstDay?.startTime || ""} - ${firstDay?.endTime || ""}`;

    return `${locationName} - ${group?.type || ""} - ${dayInfo}`;
  };

  const getTourNumber = (group) => {
    const raw = group?.tour;

    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
        ? parseInt(raw.replace(/[^\d]/g, ""), 10)
        : NaN;

    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const getGalaTimeSeconds = (group) => {
    const raw = group?.galaTime;
    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
        ? parseInt(raw.replace(/[^\d]/g, ""), 10)
        : NaN;

    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const formatSeconds = (seconds) => {
    const sec = Number(seconds) || 0;
    const min = Math.floor(sec / 60);
    const rest = sec % 60;
    return `${min}:${String(rest).padStart(2, "0")} (${sec}s)`;
  };

  const getRocznikowyWiek = (dateOfBirth) => {
    if (!dateOfBirth || typeof dateOfBirth !== "string") return null;
    const year = parseInt(dateOfBirth.split("-")[0], 10);
    if (!Number.isFinite(year)) return null;
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    return age >= 0 && age <= 100 ? age : null;
  };

  const setGroupTour = async (groupId, tourNumber) => {
    if (!groupId) return;
    if (!Number.isFinite(tourNumber) || tourNumber <= 0) return;

    try {
      await setDoc(
        doc(db, "groups", FACILITY_ID),
        {
          [groupId]: { tour: tourNumber },
        },
        { merge: true }
      );

      setTourGroup(null);
      setTourInput("");
    } catch (error) {
      console.error("Błąd przy ustawianiu tour", error);
    }
  };

  const setGroupGalaTime = async (groupId, galaTime) => {
    if (!groupId) return;
    if (!Number.isFinite(galaTime) || galaTime < 0) return;

    try {
      await setDoc(
        doc(db, "groups", FACILITY_ID),
        {
          [groupId]: { galaTime: String(galaTime) },
        },
        { merge: true }
      );

      setTimeGroup(null);
      setTimeInput("");
    } catch (error) {
      console.error("Błąd przy ustawianiu galaTime", error);
    }
  };

  // ---- SUMMARY BUILD ----
  const groupsSummary = useMemo(() => {
    if (!groups?.length) return [];

    return groups
      .map((group) => {
        const locationName =
          locations?.find((x) => x?.id === group?.location_id)?.name || "Brak lokalizacji";

        return {
          ...group,
          locationName,
          label: getGroupLabel(group),
          tourNumber: getTourNumber(group),
          galaTimeSeconds: getGalaTimeSeconds(group),
        };
      })
      .map((group) => {
        const usersInGroup = users.filter((u) => u?.user?.groups?.includes(group?.id));

        let green = 0;
        let red = 0;
        let black = 0;
        let yellow = 0;

        usersInGroup.forEach((u) => {
          const status = getSingleGroupGalaStatus(u, group.id);
          if (status === "green") green += 1;
          else if (status === "red") red += 1;
          else if (status === "black") black += 1;
          else if (status === "yellow") yellow += 1;
        });

        return {
          ...group,
          total: usersInGroup.length,
          green,
          red,
          black,
          yellow,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "pl"));
  }, [groups, users, locations]);

  

  const jastrzebieGroups = useMemo(
    () => groupsSummary.filter((g) => isJastrzebieLocation(g.locationName)),
    [groupsSummary]
  );
  const wroclawGroups = useMemo(
    () => groupsSummary.filter((g) => !isJastrzebieLocation(g.locationName)),
    [groupsSummary]
  );

  const jastrzebieActiveGroups = useMemo(
    () => jastrzebieGroups.filter((g) => g?.gala !== false),
    [jastrzebieGroups]
  );
  const jastrzebieHiddenGroups = useMemo(
    () => jastrzebieGroups.filter((g) => g?.gala === false),
    [jastrzebieGroups]
  );

  const wroclawActiveGroups = useMemo(
    () => wroclawGroups.filter((g) => g?.gala !== false),
    [wroclawGroups]
  );
  const wroclawHiddenGroups = useMemo(
    () => wroclawGroups.filter((g) => g?.gala === false),
    [wroclawGroups]
  );

  const activeList = activeTab === "jastrzebie" ? jastrzebieActiveGroups : wroclawActiveGroups;
  const hiddenList = activeTab === "jastrzebie" ? jastrzebieHiddenGroups : wroclawHiddenGroups;
  const tabTitle = activeTab === "jastrzebie" ? "Jastrzębie" : "Wrocław";

  const toursInTab = useMemo(() => {
    const set = new Set(activeList.map((g) => g?.tourNumber).filter((x) => Number.isFinite(x)));
    return Array.from(set).sort((a, b) => a - b);
  }, [activeList]);

  const activeWithoutTour = useMemo(
    () => activeList.filter((g) => !Number.isFinite(g?.tourNumber)),
    [activeList]
  );

  const hiddenWithTourNumbers = useMemo(() => {
    const set = new Set(hiddenList.map((g) => g?.tourNumber).filter((x) => Number.isFinite(x)));
    return Array.from(set).sort((a, b) => a - b);
  }, [hiddenList]);

  const hiddenWithoutTour = useMemo(
    () => hiddenList.filter((g) => !Number.isFinite(g?.tourNumber)),
    [hiddenList]
  );

  const tabSummary = useMemo(() => {
    return activeList.reduce(
      (acc, group) => {
        acc.groups += 1;
        acc.total += group.total || 0;
        acc.green += group.green || 0;
        acc.red += group.red || 0;
        acc.yellow += group.yellow || 0;
        acc.black += group.black || 0;
        return acc;
      },
      { groups: 0, total: 0, green: 0, red: 0, yellow: 0, black: 0 }
    );
  }, [activeList]);

  const getTourSummary = (list) => {
    return list.reduce(
      (acc, group) => {
        acc.people += group.green || 0;
        acc.performances += 1;
        acc.time += group.galaTimeSeconds || 0;
        return acc;
      },
      { people: 0, performances: 0, time: 0 }
    );
  };

  const getUserUniqueKey = (u) => {
    return `${u?.uid || ""}-${u?.user?.index ?? u?.user?.displayName ?? ""}`;
  };

  const jastrzebieActiveGroupIds = useMemo(() => {
    return new Set(jastrzebieActiveGroups.map((g) => g.id).filter(Boolean));
  }, [jastrzebieActiveGroups]);

  const wroclawActiveGroupIds = useMemo(() => {
    return new Set(wroclawActiveGroups.map((g) => g.id).filter(Boolean));
  }, [wroclawActiveGroups]);

const buildAgeStatsForGroupSet = (groupIdsSet) => {
  const countedUsers = new Map();
  const ageMap = new Map();

  users.forEach((u) => {
    const userGroups = Array.isArray(u?.user?.groups) ? u.user.groups : [];

    const takesPartInGala = userGroups.some((gid) => {
      if (!groupIdsSet.has(gid)) return false;
      return u?.user?.gala?.[gid] === true;
    });

    if (!takesPartInGala) return;

    const uniqueKey = getUserUniqueKey(u);
    if (countedUsers.has(uniqueKey)) return;

    countedUsers.set(uniqueKey, true);

    const age = getRocznikowyWiek(u?.user?.dateOfBirth);
    if (age === null) return;

    ageMap.set(age, (ageMap.get(age) || 0) + 1);
  });

  return Array.from(ageMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([age, count]) => ({ age, count }));
};
  const jastrzebieAgeStats = useMemo(() => {
    return buildAgeStatsForGroupSet(jastrzebieActiveGroupIds);
  }, [users, jastrzebieActiveGroupIds]);

  const wroclawAgeStats = useMemo(() => {
    return buildAgeStatsForGroupSet(wroclawActiveGroupIds);
  }, [users, wroclawActiveGroupIds]);

  // ---- UI PIECES ----
  const TabButton = ({ id, label }) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          padding: "10px 14px",
          borderRadius: "10px",
          border: isActive ? "2px solid rgb(30,30,30)" : "1px solid rgb(30,30,30,0.2)",
          backgroundColor: isActive ? "rgb(30,30,30)" : "#fff",
          color: isActive ? "#fff" : "rgb(30,30,30)",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };

  const renderSummaryBox = (summary) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "18px",
        padding: "12px 14px",
        border: "1px solid rgb(30,30,30,0.12)",
        borderRadius: "8px",
        backgroundColor: "rgb(248,248,248)",
        marginTop: "6px",
      }}
    >
      <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
        Grupy: <b>{summary.groups}</b>
      </h3>

      <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
        Uczestnicy: <b>{summary.total}</b>
      </h3>

      <h3 style={{ fontSize: "13px", color: "#2e9428", margin: 0 }}>
        Występuje: <b>{summary.green}</b>
      </h3>

      <h3 style={{ fontSize: "13px", color: "#DC143C", margin: 0 }}>
        Nie występuje: <b>{summary.red}</b>
      </h3>

      <h3 style={{ fontSize: "13px", color: "rgb(30,30,30)", margin: 0 }}>
        Brak deklaracji: <b>{summary.black}</b>
      </h3>
    </div>
  );

  const renderTourBottomSummary = (tourGroups) => {
    const summary = getTourSummary(tourGroups);

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "18px",
          padding: "10px 14px",
          border: "1px solid rgb(30,30,30,0.10)",
          borderRadius: "8px",
          backgroundColor: "rgb(248,248,248)",
        }}
      >
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30)", margin: 0 }}>
          Osoby występujące: <b>{summary.people}</b>
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30)", margin: 0 }}>
          Ilość występów: <b>{summary.performances}</b>
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30)", margin: 0 }}>
          Ilość czasu: <b>{formatSeconds(summary.time)}</b>
        </h3>
      </div>
    );
  };

  const renderGroupRow = (group) => (
    <div
      key={group.id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
        padding: "14px 16px",
        border: "1px solid rgb(30,30,30,0.15)",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: "4px",
          width: "40%",
        }}
      >
        <h3 style={{ fontSize: "14px", color: "rgb(30,30,30)", margin: 0 }}>
          {group.label}
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Łącznie osób: {group.total}
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          {group.id}
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Tura: <b>{group.tourNumber ?? "-"}</b>
        </h3>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Czas występu: <b>{group.galaTimeSeconds > 0 ? formatSeconds(group.galaTimeSeconds) : "-"}</b>
        </h3>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "18px",
          flexWrap: "wrap",
          width: "22%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="#2e9428" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.green}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="#DC143C" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.red}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="rgb(30,30,30)" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.black}</span>
        </div>
      </div>

      <div
        style={{
          width: "38%",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => {
            setTourGroup(group);
            setTourInput(String(group.tourNumber ?? ""));
          }}
          style={{
            fontSize: "12px",
            color: "white",
            borderRadius: "5px",
            backgroundColor: "#0045C7",
            border: "2px solid #0045C7",
            fontWeight: "bold",
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Ustaw turę
        </button>

        <button
          onClick={() => {
            setTimeGroup(group);
            setTimeInput(String(group.galaTimeSeconds || ""));
          }}
          style={{
            fontSize: "12px",
            color: "white",
            borderRadius: "5px",
            backgroundColor: "#2e9428",
            border: "2px solid #2e9428",
            fontWeight: "bold",
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Dodaj czas występu
        </button>
      </div>
    </div>
  );

  const renderHiddenGroupRow = (group) => (
    <div
      key={group.id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
        padding: "14px 16px",
        border: "1px solid rgba(30,30,30,0.10)",
        borderRadius: "8px",
        backgroundColor: "rgb(245,245,245)",
        opacity: 0.6,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "45%" }}>
        <h3 style={{ fontSize: "14px", color: "rgb(30,30,30)", margin: 0 }}>
          {group.label}
        </h3>
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Łącznie osób: {group.total}
        </h3>
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          {group.id}
        </h3>
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Tura: <b>{group.tourNumber ?? "-"}</b>
        </h3>
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.65)", margin: 0 }}>
          Czas występu: <b>{group.galaTimeSeconds > 0 ? formatSeconds(group.galaTimeSeconds) : "-"}</b>
        </h3>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "18px", flexWrap: "wrap", width: "35%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="#2e9428" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.green}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="#DC143C" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.red}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Gala wth={"20px"} color="rgb(30,30,30)" curs={"default"} />
          <span style={{ fontSize: "13px", color: "rgb(30,30,30)" }}>{group.black}</span>
        </div>
      </div>

      <div style={{ width: "20%", display: "flex", justifyContent: "flex-end" }}>
        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.6)", margin: 0 }}>Ukryta</h3>
      </div>
    </div>
  );

  const renderToursSection = () => {
    const parts = [];

    if (toursInTab.length) {
      toursInTab.forEach((tourNo) => {
        const activeInTour = activeList.filter((g) => g?.tourNumber === tourNo);
        if (!activeInTour.length) return;

        parts.push(
          <div key={`tour-${tourNo}`} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <h3 style={{ fontSize: "16px", color: "rgb(30,30,30)", margin: "4px 0 0 0" }}>
              Tura {tourNo} <span style={{ color: "rgb(30,30,30,0.55)" }}>({activeInTour.length})</span>
            </h3>
            {activeInTour.map(renderGroupRow)}
            {renderTourBottomSummary(activeInTour)}
          </div>
        );
      });
    }

    if (activeWithoutTour.length) {
      parts.push(
        <div key="no-tour-active" style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
          <h3 style={{ fontSize: "16px", color: "rgb(30,30,30)", margin: "8px 0 0 0" }}>
            Grupy bez przypisanej tury <span style={{ color: "rgb(30,30,30,0.55)" }}>({activeWithoutTour.length})</span>
          </h3>
          {activeWithoutTour.map(renderGroupRow)}
          {renderTourBottomSummary(activeWithoutTour)}
        </div>
      );
    }

    if (hiddenList.length) {
      parts.push(
        <div key="hidden-all" style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "12px" }}>
          <h3 style={{ fontSize: "14px", color: "rgb(30,30,30,0.55)", margin: "8px 0 0 0" }}>
            Ukryte grupy <span style={{ color: "rgb(30,30,30,0.55)" }}>({hiddenList.length})</span>
          </h3>

          {hiddenWithTourNumbers.map((tourNo) => {
            const hiddenInTour = hiddenList.filter((g) => g?.tourNumber === tourNo);
            if (!hiddenInTour.length) return null;

            return (
              <div key={`hidden-tour-${tourNo}`} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.55)", margin: 0 }}>
                  Tura {tourNo} <span style={{ color: "rgb(30,30,30,0.55)" }}>({hiddenInTour.length})</span>
                </h3>
                {hiddenInTour.map(renderHiddenGroupRow)}
                {renderTourBottomSummary(hiddenInTour)}
              </div>
            );
          })}

          {hiddenWithoutTour.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.55)", margin: 0 }}>
                Bez przypisanej tury <span style={{ color: "rgb(30,30,30,0.55)" }}>({hiddenWithoutTour.length})</span>
              </h3>
              {hiddenWithoutTour.map(renderHiddenGroupRow)}
              {renderTourBottomSummary(hiddenWithoutTour)}
            </div>
          )}
        </div>
      );
    }

    if (!parts.length) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {activeList.map(renderGroupRow)}
          {activeList.length > 0 && renderTourBottomSummary(activeList)}

          {hiddenList.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "8px" }}>
              <h3 style={{ fontSize: "14px", color: "rgb(30,30,30,0.55)", margin: 0 }}>
                Ukryte grupy <span style={{ color: "rgb(30,30,30,0.55)" }}>({hiddenList.length})</span>
              </h3>
              {hiddenList.map(renderHiddenGroupRow)}
              {renderTourBottomSummary(hiddenList)}
            </div>
          )}
        </div>
      );
    }

    return parts;
  };

  return (
    <div
      className={isToogle ? "side-toogle-margin" : "side-margin"}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        minHeight: "100vh",
        overflowY: "scroll",
        gap: "20px",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
          gap: "16px",
        }}
      >
        <h2 style={{ fontSize: "22px", color: "rgb(30,30,30)" }}>
          Gala – podsumowanie grup
        </h2>

        <div style={{ width: "92%", maxWidth: "1200px", display: "flex", gap: "10px" }}>
          <TabButton id="jastrzebie" label="Jastrzębie" />
          <TabButton id="wroclaw" label="Wrocław" />
        </div>

        <div
          style={{
            width: "92%",
            maxWidth: "1200px",
            display: "flex",
            flexDirection: "column",
            gap: "26px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <h3 style={{ fontSize: "18px", color: "rgb(30,30,30)", margin: 0 }}>
              {tabTitle}
            </h3>

            {renderToursSection()}

            {renderSummaryBox(tabSummary)}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              marginTop: "10px",
            }}
          >
            <h3 style={{ fontSize: "18px", color: "rgb(30,30,30)", margin: 0 }}>
              Lista wiekowa
            </h3>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <h3 style={{ fontSize: "15px", color: "rgb(30,30,30)", margin: 0 }}>
                Jastrzębie
              </h3>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "12px 14px",
                  border: "1px solid rgb(30,30,30,0.12)",
                  borderRadius: "8px",
                  backgroundColor: "rgb(248,248,248)",
                }}
              >
                {jastrzebieAgeStats.length > 0 ? (
                  jastrzebieAgeStats.map((item) => (
                    <h3
                      key={`j-${item.age}`}
                      style={{
                        fontSize: "13px",
                        color: "rgb(30,30,30)",
                        margin: 0,
                      }}
                    >
                      {item.age} lat: <b>{item.count}</b>
                    </h3>
                  ))
                ) : (
                  <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.6)", margin: 0 }}>
                    Brak danych wiekowych
                  </h3>
                )}
              </div>

              <h3 style={{ fontSize: "15px", color: "rgb(30,30,30)", margin: "6px 0 0 0" }}>
                Wrocław
              </h3>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "12px 14px",
                  border: "1px solid rgb(30,30,30,0.12)",
                  borderRadius: "8px",
                  backgroundColor: "rgb(248,248,248)",
                }}
              >
                {wroclawAgeStats.length > 0 ? (
                  wroclawAgeStats.map((item) => (
                    <h3
                      key={`w-${item.age}`}
                      style={{
                        fontSize: "13px",
                        color: "rgb(30,30,30)",
                        margin: 0,
                      }}
                    >
                      {item.age} lat: <b>{item.count}</b>
                    </h3>
                  ))
                ) : (
                  <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.6)", margin: 0 }}>
                    Brak danych wiekowych
                  </h3>
                )}
              </div>
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: "12px", color: "rgb(30,30,30,0.6)" }}>
          Ilość grup: {groupsSummary.length}
        </h3>
      </div>

      {tourGroup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 60,
          }}
        >
          <div
            style={{
              width: "90%",
              maxWidth: "520px",
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ fontSize: "20px", color: "rgb(30,30,30)", margin: 0 }}>
              Ustaw numer tury
            </h3>

            <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.7)", margin: 0, lineHeight: 1.5 }}>
              Grupa: <b>{tourGroup.label || getGroupLabel(tourGroup)}</b>
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", color: "rgb(30,30,30,0.75)", fontWeight: "bold" }}>
                Numer tury
              </label>
              <input
                value={tourInput}
                onChange={(e) => setTourInput(e.target.value)}
                placeholder="np. 3"
                inputMode="numeric"
                style={{
                  width: "100%",
                  border: "1px solid rgba(30,30,30,0.2)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
              <button
                onClick={() => {
                  setTourGroup(null);
                  setTourInput("");
                }}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgba(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={() => {
                  const n = parseInt(String(tourInput || "").replace(/[^\d]/g, ""), 10);
                  if (!Number.isFinite(n) || n <= 0) return;
                  setGroupTour(tourGroup.id, n);
                }}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#0045C7",
                  border: "2px solid #0045C7",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Zapisz turę
              </button>
            </div>
          </div>
        </div>
      )}

      {timeGroup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 60,
          }}
        >
          <div
            style={{
              width: "90%",
              maxWidth: "520px",
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ fontSize: "20px", color: "rgb(30,30,30)", margin: 0 }}>
              Dodaj czas występu
            </h3>

            <h3 style={{ fontSize: "13px", color: "rgb(30,30,30,0.7)", margin: 0, lineHeight: 1.5 }}>
              Grupa: <b>{timeGroup.label || getGroupLabel(timeGroup)}</b>
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", color: "rgb(30,30,30,0.75)", fontWeight: "bold" }}>
                Czas w sekundach
              </label>
              <input
                value={timeInput}
                onChange={(e) => setTimeInput(e.target.value)}
                placeholder="np. 95"
                inputMode="numeric"
                style={{
                  width: "100%",
                  border: "1px solid rgba(30,30,30,0.2)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              <div style={{ fontSize: "11px", color: "rgb(30,30,30,0.6)" }}>
                Aktualnie: <b>{timeGroup.galaTimeSeconds > 0 ? formatSeconds(timeGroup.galaTimeSeconds) : "-"}</b>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "4px" }}>
              <button
                onClick={() => {
                  setTimeGroup(null);
                  setTimeInput("");
                }}
                style={{
                  fontSize: "12px",
                  color: "rgb(30,30,30)",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid rgba(30,30,30,0.2)",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Anuluj
              </button>

              <button
                onClick={() => {
                  const n = parseInt(String(timeInput || "").replace(/[^\d]/g, ""), 10);
                  if (!Number.isFinite(n) || n < 0) return;
                  setGroupGalaTime(timeGroup.id, n);
                }}
                style={{
                  fontSize: "12px",
                  color: "white",
                  borderRadius: "5px",
                  backgroundColor: "#2e9428",
                  border: "2px solid #2e9428",
                  fontWeight: "bold",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                Zapisz czas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}